/**
 * IAM API Axios Instance
 * Dedicated axios instance for IAM-related API calls
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { v4 as uuidv4 } from "uuid";
import { addAuthHeader, handle401Error } from "../utils/auth/axiosInterceptor";

// Ensure baseURL always includes /v1/accountManagement (full path: {origin}/v1/accountManagement/accounts/...)
const rawBase = import.meta.env.VITE_REACT_ACCOUNT_APP_API || "http://localhost:9190";
const baseURL = rawBase.endsWith("/v1/accountManagement")
  ? rawBase
  : rawBase.replace(/\/?$/, "") + "/v1/accountManagement";

const iamHttps = axios.create({
  baseURL,
  timeout: 50000,
});

// Add request interceptor: mandatory headers (all requests including invitation) and optional Authorization
iamHttps.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Mandatory for all requests: X-Message-Id, X-Correlation-Id (exact casing; some backends are strict)
    const headers = config.headers || (config.headers = {} as any);
    const messageId = typeof headers["X-Message-Id"] === "string" ? headers["X-Message-Id"] : uuidv4();
    const correlationId = typeof headers["X-Correlation-Id"] === "string" ? headers["X-Correlation-Id"] : uuidv4();
    headers["X-Message-Id"] = messageId;
    headers["X-Correlation-Id"] = correlationId;
    // Skip Authorization for invitation flow (GET/POST .../accounts/invitation or .../accounts/invitation/{token}) – unauthenticated
    const url = config.url || "";
    const isInvitationRequest = url.includes("accounts/invitation");
    if (!isInvitationRequest) {
      return addAuthHeader(config);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
iamHttps.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Skip token refresh for login/refresh and invitation (unauthenticated) endpoints
      const url = originalRequest.url || "";
      if (url.includes("/auth/login") || url.includes("/auth/token/refresh") || url.includes("accounts/invitation")) {
        return Promise.reject(error);
      }

      try {
        return await handle401Error(error, (config) => iamHttps.request(config));
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default iamHttps;
